'use no memo';
import type { FC } from 'react';
import React, { useMemo, useState } from 'react';
import { cssMap } from '@compiled/react';
import debounce from 'lodash/debounce';

import { token } from '@atlaskit/tokens';
import { WidthObserver } from '@atlaskit/width-detector';

import { PageCard } from './PageCard.tsx';
import type { PageCard as PageCardType, PageCardAppearance } from './card-types.ts';
import { PageCardsContext } from './PageCardsContext.ts';

const pageCardConainerStyles = cssMap({
	grid: {
		display: 'grid',
		minWidth: '295px',
		maxWidth: '1320px',
		gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
		gap: token('space.200'),
	},
	list: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: '600px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div': {
			borderRadius: '0',
			borderBottom: 'none',
			'&:hover': {
				boxShadow: 'none',
				backgroundColor: token('color.background.accent.gray.subtlest'),
				'--secondary-text-color': token('color.text.subtle'),
			},
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'& > div:first-of-type': {
			borderTopLeftRadius: token('radius.large'),
			borderTopRightRadius: token('radius.large'),
			borderBottomRightRadius: '0',
			borderBottomLeftRadius: '0',
			borderBottom: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'& > div:nth-last-of-type(2)': {
			borderBottom: `${token('border.width')} solid ${token('color.border')}`,
			borderTopLeftRadius: '0',
			borderTopRightRadius: '0',
			borderBottomRightRadius: token('radius.large'),
			borderBottomLeftRadius: token('radius.large'),
		},
	},
	'compact-list': {},
});

export type AnalyticsData = {
	source?: string;
	attributes?: { [key: string]: any };
};

type PageCardsProps = {
	appearance: PageCardAppearance;
	nodes: PageCardType[];
	analyticsData?: AnalyticsData;
};

export const PageCards: FC<PageCardsProps> = ({ appearance, nodes, analyticsData }) => {
	const [width, setWidth] = useState(0);
	const onResize = debounce(setWidth, 200, { leading: false });

	const pageCardsContextValue = useMemo(() => ({ width }), [width]);

	return (
		<PageCardsContext.Provider value={pageCardsContextValue}>
			<div css={[pageCardConainerStyles[appearance]]} role="list">
				{nodes.map((node, i) => (
					<PageCard
						appearance={appearance}
						data={node}
						key={node.key || node.id}
						ranking={i}
						analyticsData={analyticsData}
					/>
				))}
				<WidthObserver setWidth={onResize} offscreen />
			</div>
		</PageCardsContext.Provider>
	);
};
